import React, { useEffect, useState } from 'react'
import { useAuthContext } from '../../../../hooks/useAuthContext'
import { useParams } from 'react-router-dom'
import { userAssignmentService } from '../../../../services/userAssignmentService'
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import ClearIcon from '@mui/icons-material/Clear'
import SelectCombinationModal from './components/SelectCombinationModal'
import UserAssignmentChips from './components/UserAssignmentChips'
import ClearAllConfirmationModal from './components/ClearAllConfirmationModal'
import { formatUserDisplayName } from '../../../../utility/userName'

export const getUnAssignedCombination = (combinations, userAssignmentData) => {
  // Get Combination ids
  const combinationIds = combinations.map((combination) => {
    return combination.map((item) => item.list_data_id)
  })

  let alreadyAssignedCombinations = []
  userAssignmentData?.forEach((userAssignment) => {
    alreadyAssignedCombinations = [
      ...alreadyAssignedCombinations,
      ...(userAssignment?.assignments?.data_ids || []),
    ]
  })

  // Create a set to track unassigned data
  const unassigned = []
  combinationIds.forEach((combination) => {
    const isAssigned = alreadyAssignedCombinations.some(
      (assignedCombination) => JSON.stringify(assignedCombination) === JSON.stringify(combination)
    )
    if (!isAssigned) {
      unassigned.push(combination)
    }
  })

  return unassigned
}

const UserActivityAssignments = () => {
  const { user } = useAuthContext()

  const activityTemplateId = useParams().activity_template_id

  //   State Variables
  const [loading, setLoading] = useState(false)
  const [openDialog, setOpenDialog] = useState(false)
  const [userAssignmentData, setUserAssignmentData] = useState([])
  const [activityDetails, setActivityDetails] = useState({})
  const [userTeamData, setUserTeamData] = useState([])
  const [currentAssigneeIndex, setCurrentAssigneeIndex] = useState(null)
  const [listDataCombinations, setListDataCombinations] = useState([])
  const [openClearAllConfirmation, setOpenClearAllConfirmation] = useState(false)
  const [unassignedData, setUnassignedData] = useState([])
  const [assignedFieldData, setAssignedFieldData] = useState([])

  const getDataFromServer = async () => {
    try {
      setLoading(true)
      const data = await userAssignmentService.getActivityAssignments(user, activityTemplateId)
      const allowedTeamAssignmentFieldMap = data.allowedTeamAssignmentFieldMap
      const assignmentFieldData = data.assignmentFieldData

      // Generate combinations of listData for assignment fields
      let categorizedAssignmentFieldData = allowedTeamAssignmentFieldMap?.reduce((acc, field) => {
        let assignmentFieldTemplateId = field.assignment_field_template_id
        let filteredData = assignmentFieldData
          ?.filter((field) => field.field_template_id == assignmentFieldTemplateId)
          .sort((a, b) =>
            a.list_data_value.toLowerCase().localeCompare(b.list_data_value.toLowerCase())
          )
        if (
          filteredData.length > 0 &&
          !acc.some((fd) => fd[0].field_template_id == assignmentFieldTemplateId)
        ) {
          acc.push(filteredData)
        }

        return acc
      }, [])

      const combinations = getAllCombinations(categorizedAssignmentFieldData)

      // Get data only for allowed teams for assignment, even if there are multiple assigned_team_ids
      let allowedTeams = [...new Set(allowedTeamAssignmentFieldMap?.map((it) => it.team_id))]
      let allowedUserTeamData = data.userTeamData?.filter((it) => allowedTeams.includes(it.team_id))

      // Remove duplicate users caused by users being in multiple teams
      const removeDuplicates = (arr) => {
        const uniqueIds = new Set()
        return arr.filter((item) => {
          if (!uniqueIds.has(item.id)) {
            uniqueIds.add(item.id)
            return true
          }
          return false
        })
      }
      let removeDuplicateTeamData = removeDuplicates(allowedUserTeamData)

      const unassignedData = getUnAssignedCombination(combinations, data.userAssignmentData)

      setUnassignedData(unassignedData)
      setListDataCombinations(combinations)
      setUserAssignmentData(data.userAssignmentData)
      setActivityDetails(data.activityDetails)
      setUserTeamData(removeDuplicateTeamData)
      setAssignedFieldData(data.assignmentFieldData)
      setLoading(false)
    } catch (error) {
      setLoading(false)
      console.error(error)
    }
  }

  useEffect(() => {
    if (user) {
      getDataFromServer()
    }
  }, [user])

  const getAllCombinations = (arrays) => {
    //if no arrays are left, return an array with an empty array
    if (arrays.length === 0) return [[]]

    // Get the first array and the rest of the arrays
    const firstArray = arrays[0]
    const restArrays = arrays.slice(1)

    // Recursively get all combinations of the remaining arrays
    const restCombinations = getAllCombinations(restArrays)

    // Combine each item in the first array with each combination of the rest
    const allCombinations = []
    for (const item of firstArray) {
      for (const combination of restCombinations) {
        allCombinations.push([item, ...combination])
      }
    }

    return allCombinations
  }

  const handleClearAssignments = async (assignee_id) => {
    // Get Assignment Field Ids
    const assignmentFieldTemplateIds = listDataCombinations[0].map((it) => it.field_template_id)

    let userDatum = userAssignmentData?.find((user) => user.user_id == assignee_id)
    let updatedUserAssignmentData = []
    if (!userDatum) {
      userDatum = {
        assignment_for_activity_id: activityTemplateId,
        assignments: {
          field_template_ids: assignmentFieldTemplateIds,
          data_ids: [],
        },
        field_template_id: null,
        list_data_id: null,
        user_id: assignee_id,
      }
      updatedUserAssignmentData = [...userAssignmentData, userDatum]
      setUserAssignmentData([...userAssignmentData, userDatum])
    } else {
      updatedUserAssignmentData = userAssignmentData.map((user) => {
        if (user.user_id == assignee_id) {
          return {
            ...user,
            assignments: {
              field_template_ids: assignmentFieldTemplateIds,
              data_ids: [],
            },
          }
        }
        return user
      })
      setUserAssignmentData(updatedUserAssignmentData)
    }

    const unassignedData = getUnAssignedCombination(listDataCombinations, updatedUserAssignmentData)
    setUnassignedData(unassignedData)

    await userAssignmentService.saveUserActivityAssignments(user, {
      assignee_id: assignee_id,
      activity_template_id: activityTemplateId,
      assignments: { field_template_ids: assignmentFieldTemplateIds, data_ids: [] },
    })

    setOpenClearAllConfirmation(false)
  }

  if (loading) {
    return (
      <div
        style={{ height: '80vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <CircularProgress color="primary" size={40} />
      </div>
    )
  }

  return (
    <>
      {userTeamData.length > 0 ? (
        <>
          <h2>User Assignments</h2>
          <h3>
            {activityDetails.activity_template_name} - ({activityDetails.id})
          </h3>

          <TableContainer component={Paper} sx={{ maxHeight: 490 }}>
            <Table stickyHeader sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: 300, fontWeight: 'bold' }} align="center">
                    Users
                  </TableCell>
                  <TableCell style={{ maxWidth: 300, fontWeight: 'bold' }} align="center">
                    Assignments
                  </TableCell>
                  <TableCell style={{ width: 100, fontWeight: 'bold' }} align="center">
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {userTeamData.map((user, index) => (
                  <TableRow
                    key={'tr' + user.id}
                    style={{ backgroundColor: index % 2 === 0 ? 'whitesmoke' : 'white' }}
                  >
                    <TableCell component="th" scope="row">
                      {formatUserDisplayName(user)}
                    </TableCell>
                    <TableCell>
                      <UserAssignmentChips
                        userAssignmentData={userAssignmentData}
                        assignee_id={user.id}
                        assignmentFieldData={assignedFieldData}
                        setUserAssignmentData={setUserAssignmentData}
                        setUnassignedData={setUnassignedData}
                        listDataCombinations={listDataCombinations}
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Tooltip title="Add Combinations">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setOpenDialog(true)
                              setCurrentAssigneeIndex(index)
                            }}
                          >
                            <AddIcon color="primary" />
                          </IconButton>
                        </Tooltip>
                        <Divider orientation="vertical" flexItem />
                        <Tooltip title="Clear All">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setOpenClearAllConfirmation(true)
                              setCurrentAssigneeIndex(index)
                            }}
                          >
                            <ClearIcon fontSize="30px" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}

                {/* Add Unassigned Data Row */}
                {unassignedData.length > 0 && (
                  <TableRow style={{ backgroundColor: 'lightyellow' }}>
                    <TableCell component="th" scope="row" style={{ fontWeight: 'bold' }}>
                      Unassigned
                    </TableCell>
                    <TableCell colSpan={2}>
                      <UserAssignmentChips
                        userAssignmentData={userAssignmentData}
                        assignee_id={user.id}
                        assignmentFieldData={assignedFieldData}
                        setUserAssignmentData={setUserAssignmentData}
                        unassignedData={unassignedData}
                        setUnassignedData={setUnassignedData}
                        listDataCombinations={listDataCombinations}
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : (
        <>User assignment is not applicable for this activity</>
      )}

      {/* Select Assignment Combination */}
      {openDialog ? (
        <SelectCombinationModal
          listDataCombinations={listDataCombinations}
          openDialog={openDialog}
          setOpenDialog={setOpenDialog}
          assignee={userTeamData[currentAssigneeIndex]}
          activityTemplateId={activityTemplateId}
          userAssignmentData={userAssignmentData}
          setUserAssignmentData={setUserAssignmentData}
          setAssignedFieldData={setAssignedFieldData}
          userTeamData={userTeamData}
          assignmentFieldData={assignedFieldData}
          unassignedData={unassignedData}
          setUnassignedData={setUnassignedData}
        />
      ) : null}

      {/* Clear All Assignment Confirmation Dialog */}
      {openClearAllConfirmation ? (
        <ClearAllConfirmationModal
          openClearAllConfirmation={openClearAllConfirmation}
          setOpenClearAllConfirmation={setOpenClearAllConfirmation}
          assignee={userTeamData[currentAssigneeIndex]}
          handleClearAssignments={handleClearAssignments}
          unassignedData={unassignedData}
          setUnassignedData={setUnassignedData}
        />
      ) : null}
    </>
  )
}

export default UserActivityAssignments
